import { useEffect, useMemo, useState } from 'react';
import {
  Users,
  Building2,
  CheckSquare,
  ClipboardList,
  Calendar,
  Briefcase,
  FileDown,
  CalendarOff,
} from 'lucide-react';
import { getISOWeek } from 'date-fns';
import { useTranslation } from '../../i18n';
import { useTimeclockAdminStore } from '../../store/useTimeclockAdminStore';
import { usePlanningStore } from '../../store/usePlanningStore';
import { useAbsenceStore } from '../../store/useAbsenceStore';
import { useEmployeeStore } from '../../store/useEmployeeStore';
import { useDueTasksStore } from '../../store/useDueTasksStore';
import { useOpenItemsStore } from '../../store/useOpenItemsStore';

import { LivePulseHero } from '../../components/Dashboard/Admin/LivePulseHero';
import { UtilizationHeatmap } from '../../components/Dashboard/Admin/UtilizationHeatmap';
import { TodayStatusRow } from '../../components/Dashboard/Admin/TodayStatusRow';
import { AttentionCard } from '../../components/Dashboard/Admin/AttentionCard';
import { DueTasksCard } from '../../components/Dashboard/Admin/DueTasksCard';
import { OpenItemsCard } from '../../components/Dashboard/OpenItemsCard';
import { QuickTile } from '../../components/Dashboard/Admin/QuickTile';
import { SectionHead } from '../../components/Dashboard/SectionHead';

import { useDashboardOps } from '../../hooks/useDashboardOps';
import {
  buildTodayStatus,
  buildUtilizationMatrix,
  getMondayOfWeek,
  minToHHMM,
  nowMinutes as nowMinutesFn,
  weekDayIsoList,
  weekUtilization,
} from '../../utils/dashboardData';
import { isoDate } from '../../utils/dateUtils';

export function AdminDashboard() {
  const { t } = useTranslation('dashboard');

  useDashboardOps({ pollMs: 60_000 });

  const dailyStatus = useTimeclockAdminStore((s) => s.dailyStatus);
  const dailyLoading = useTimeclockAdminStore((s) => s.dailyLoading);
  const planningEntries = usePlanningStore((s) => s.entries);
  const planningLoading = usePlanningStore((s) => s.loading);
  const pendingAbsences = useAbsenceStore((s) => s.absences);
  const employees = useEmployeeStore((s) => s.employees);
  // Nur Aufgaben mit Terminregel sind hier "fällig"; Aufgaben ohne Regel liefert der
  // Endpoint zwar mit, sie gehören aber nur ins Planungs-Sidepanel.
  const allDueTasks = useDueTasksStore((s) => s.dueTasks);
  const dueTasks = useMemo(() => allDueTasks.filter((t) => t.hasSchedule), [allDueTasks]);
  const myOpenItems = useOpenItemsStore((s) => s.myItems);
  const initialLoading = (dailyLoading || planningLoading) && employees.length === 0;

  const [nowMin, setNowMin] = useState<number>(() => nowMinutesFn());
  useEffect(() => {
    const id = setInterval(() => setNowMin(nowMinutesFn()), 60_000);
    return () => clearInterval(id);
  }, []);

  const today = useMemo(() => new Date(), []);
  const todayIso = useMemo(() => isoDate(today), [today]);
  const monday = useMemo(() => getMondayOfWeek(today), [today]);
  const weekDays = useMemo(() => weekDayIsoList(monday), [monday]);
  const weekNumber = useMemo(() => getISOWeek(today), [today]);

  const todayPlannings = useMemo(
    () => planningEntries.filter((e) => e.date === todayIso),
    [planningEntries, todayIso],
  );

  const todayAbsences = useMemo(
    () =>
      pendingAbsences.filter(
        (a) =>
          a.startDate &&
          a.endDate &&
          a.startDate <= todayIso &&
          a.endDate >= todayIso,
      ),
    [pendingAbsences, todayIso],
  );

  const todayStatusRows = useMemo(
    () =>
      buildTodayStatus(
        employees,
        dailyStatus?.employees ?? [],
        todayPlannings,
        todayAbsences,
        todayIso,
        nowMin,
      ),
    [employees, dailyStatus, todayPlannings, todayAbsences, todayIso, nowMin],
  );

  const matrix = useMemo(
    () => buildUtilizationMatrix(employees, planningEntries, weekDays),
    [employees, planningEntries, weekDays],
  );

  const heroKpis = useMemo(() => {
    let plannedHours = 0;
    let taskCount = 0;
    for (const p of todayPlannings) {
      plannedHours += p.plannedHours ?? 0;
      taskCount += p.tasks?.length ?? 0;
    }
    return {
      plannedHours,
      taskCount,
      weekUtilization: weekUtilization(matrix),
    };
  }, [todayPlannings, matrix]);

  const conflicts = useMemo(() => {
    return planningEntries
      .filter((e) => e.date === todayIso && e.absenceTypeId && (e.tasks?.length ?? 0) > 0)
      .map((e) => ({
        id: e.planningEntryId ?? `${e.employeeId}-${e.date}`,
        employeeName: e.employeeName ?? '—',
        date: e.date ?? todayIso,
        message: t('admin.heatmap.legend.conflict'),
      }));
  }, [planningEntries, todayIso, t]);

  const sortedRows = useMemo(() => {
    const rank: Record<typeof todayStatusRows[number]['status'], number> = {
      in: 0,
      break: 1,
      missing: 2,
      absent: 3,
      out: 4,
    };
    return [...todayStatusRows].sort((a, b) => {
      const ra = rank[a.status];
      const rb = rank[b.status];
      if (ra !== rb) return ra - rb;
      return `${a.employee.firstName} ${a.employee.lastName}`.localeCompare(
        `${b.employee.firstName} ${b.employee.lastName}`,
      );
    });
  }, [todayStatusRows]);

  if (initialLoading) {
    return <DashboardSkeleton />;
  }

  return (
    <div className="space-y-4 sm:space-y-6 animate-fade-in">
      <LivePulseHero rows={todayStatusRows} kpis={heroKpis} clockText={minToHHMM(nowMin)} />

      <div className="grid grid-cols-1 xl:grid-cols-[1.5fr_1fr] gap-4 sm:gap-6">
        <UtilizationHeatmap matrix={matrix} weekDayIso={weekDays} weekNumber={weekNumber} />

        <div className="rounded-xl border border-border bg-surface dark:bg-slate-900 dark:border-slate-700 p-4 sm:p-5">
          <SectionHead kicker={t('admin.today.kicker')} title={t('admin.today.title')} />
          <div className="mt-3 max-h-[420px] overflow-y-auto pr-1">
            {sortedRows.length === 0 ? (
              <div className="rounded-lg border border-dashed border-border bg-surface-muted dark:bg-slate-800/50 p-5 text-center text-sm text-ink-muted dark:text-slate-400">
                {t('admin.today.empty')}
              </div>
            ) : (
              sortedRows.map((row) => <TodayStatusRow key={row.employeeId} row={row} />)
            )}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4 sm:gap-6">
        <OpenItemsCard items={myOpenItems} />
        <AttentionCard conflicts={conflicts} approvals={pendingAbsences} />
        <DueTasksCard dueTasks={dueTasks} />
      </div>

      <div className="rounded-xl border border-slate-200 bg-white p-4 sm:p-5">
        <SectionHead
          kicker={t('admin.quickTiles.kicker')}
          title={t('admin.quickTiles.title')}
          className="mb-3"
        />
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-4">
          <QuickTile
            to="/customers"
            label={t('admin.quickTiles.customers')}
            icon={Users}
            accent="primary"
          />
          <QuickTile
            to="/objects"
            label={t('admin.quickTiles.objects')}
            icon={Building2}
            accent="accent"
          />
          <QuickTile
            to="/tasks"
            label={t('admin.quickTiles.tasks')}
            icon={CheckSquare}
            accent="warning"
          />
          <QuickTile
            to="/services"
            label={t('admin.quickTiles.services')}
            icon={ClipboardList}
            accent="success"
          />
          <QuickTile
            to="/planning"
            label={t('admin.quickTiles.planning')}
            icon={Calendar}
            accent="info"
          />
          <QuickTile
            to="/employees"
            label={t('admin.quickTiles.employees')}
            icon={Briefcase}
            accent="primary"
          />
          <QuickTile
            to="/absences"
            label={t('navigation:menu.absences')}
            icon={CalendarOff}
            accent="accent"
          />
          <QuickTile
            to="/timeclock"
            label={t('admin.quickTiles.reports')}
            icon={FileDown}
            accent="neutral"
          />
        </div>
      </div>
    </div>
  );
}

function DashboardSkeleton() {
  return (
    <div className="space-y-4 sm:space-y-6 animate-pulse" aria-hidden>
      <div className="h-40 rounded-2xl bg-surface-sunken dark:bg-slate-800" />
      <div className="grid grid-cols-1 xl:grid-cols-[1.5fr_1fr] gap-4 sm:gap-6">
        <div className="h-72 rounded-xl bg-surface-sunken dark:bg-slate-800" />
        <div className="h-72 rounded-xl bg-surface-sunken dark:bg-slate-800" />
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4 sm:gap-6">
        <div className="h-48 rounded-xl bg-surface-sunken dark:bg-slate-800" />
        <div className="h-48 rounded-xl bg-surface-sunken dark:bg-slate-800" />
        <div className="h-48 rounded-xl bg-surface-sunken dark:bg-slate-800" />
      </div>
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
        {Array.from({ length: 8 }).map((_, i) => (
          <div key={i} className="h-20 rounded-xl bg-surface-sunken dark:bg-slate-800" />
        ))}
      </div>
    </div>
  );
}
